import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { APP_VERSION } from '@/lib/version';
import { Capacitor } from '@capacitor/core';

interface VersionCheckResult {
  needsUpdate: boolean;
  latestVersion: string | null;
  releaseNotes: string | null;
  storeUrl: string | null;
  iosStoreUrl: string | null;
  androidStoreUrl: string | null;
}

function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
}

export function useVersionCheck(): VersionCheckResult {
  const [result, setResult] = useState<VersionCheckResult>({
    needsUpdate: false,
    latestVersion: null,
    releaseNotes: null,
    storeUrl: null,
    iosStoreUrl: null,
    androidStoreUrl: null,
  });

  useEffect(() => {
    const check = async () => {
      try {
        const { data, error } = await supabase
          .from('app_versions')
          .select('version_code, min_supported_version, is_force_update, release_notes, ios_store_url, android_store_url')
          .eq('environment', 'production')
          .order('build_number', { ascending: false })
          .limit(1)
          .single();

        if (error || !data) return;

        const minVersion = data.min_supported_version;
        const isForce = data.is_force_update;

        if (minVersion && compareSemver(APP_VERSION, minVersion) < 0 && isForce) {
          const platform = Capacitor.getPlatform();
          const storeUrl = platform === 'ios'
            ? data.ios_store_url
            : data.android_store_url;

          setResult({
            needsUpdate: true,
            latestVersion: data.version_code,
            releaseNotes: data.release_notes,
            storeUrl: storeUrl || null,
            iosStoreUrl: data.ios_store_url || null,
            androidStoreUrl: data.android_store_url || null,
          });
        }
      } catch (e) {
        console.error('Version check failed:', e);
      }
    };

    check();
  }, []);

  return result;
}
